/**
 * VideoPlayer Component
 * Reusable video player supporting YouTube, Vimeo, and self-hosted videos
 */

import React, { useState, useRef, useEffect } from 'react';

// Detect video source type
function detectVideoType(url) {
  if (!url) return null;
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
  if (url.includes('vimeo.com')) return 'vimeo';
  return 'direct';
}

// Extract YouTube video ID
function extractYouTubeId(url) {
  if (!url) return '';
  // Handle youtube.com/watch?v=ID format
  const watchMatch = url.match(/youtube\.com\/watch\?v=([^&\s]+)/);
  if (watchMatch) return watchMatch[1];
  // Handle youtu.be/ID format
  const shortMatch = url.match(/youtu\.be\/([^?\s]+)/);
  if (shortMatch) return shortMatch[1];
  // Handle youtube.com/shorts/ID format, including URLs copied from the
  // YouTube Shorts player. Shorts use the same video ID in a normal embed.
  const shortsMatch = url.match(/youtube\.com\/shorts\/([^/?\s]+)/);
  if (shortsMatch) return shortsMatch[1];
  // Handle youtube.com/embed/ID format
  const embedMatch = url.match(/youtube\.com\/embed\/([^?\s]+)/);
  if (embedMatch) return embedMatch[1];
  return '';
}

// Extract Vimeo video ID
function extractVimeoId(url) {
  if (!url) return '';
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match?.[1] || '';
}

function VideoPlayer({
  url,
  autoplay = true,
  muted = true,
  loop = true,
  controls = false,
  poster,
  className = '',
  style = {},
  onPlay,
  onPause,
  onEnded,
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef(null);
  const videoType = detectVideoType(url);

  // Handle intersection observer for lazy loading
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  if (!url || !videoType) {
    return (
      <div
        ref={containerRef}
        className={`vp-placeholder ${className}`}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#1a1a2e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
          ...style,
        }}
      >
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <polygon points="5 3 19 12 5 21 5 3" />
        </svg>
      </div>
    );
  }

  // YouTube embed
  if (videoType === 'youtube') {
    const videoId = extractYouTubeId(url);
    if (!videoId) return null;

    const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      mute: muted ? '1' : '0',
      loop: loop ? '1' : '0',
      controls: controls ? '1' : '0',
      playlist: videoId, // Required for loop to work
      modestbranding: '1',
      rel: '0',
      showinfo: '0',
      iv_load_policy: '3', // Hide annotations
      playsinline: '1',
    });

    return (
      <div ref={containerRef} className={className} style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
        {poster && !isLoaded && (
          <img
            src={poster}
            alt="Video poster"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 1,
            }}
          />
        )}
        {isVisible && (
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?${params.toString()}`}
            allow="autoplay; encrypted-media; accelerometer; gyroscope; picture-in-picture"
            allowFullScreen
            onLoad={() => setIsLoaded(true)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              zIndex: 2,
            }}
            title="YouTube video"
          />
        )}
      </div>
    );
  }

  // Vimeo embed
  if (videoType === 'vimeo') {
    const videoId = extractVimeoId(url);
    if (!videoId) return null;

    const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      muted: muted ? '1' : '0',
      loop: loop ? '1' : '0',
      controls: controls ? '1' : '0',
      background: !controls ? '1' : '0',
      title: '0',
      byline: '0',
      portrait: '0',
    });

    return (
      <div ref={containerRef} className={className} style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
        {poster && !isLoaded && (
          <img
            src={poster}
            alt="Video poster"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 1,
            }}
          />
        )}
        {isVisible && (
          <iframe
            src={`https://player.vimeo.com/video/${videoId}?${params.toString()}`}
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            onLoad={() => setIsLoaded(true)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              zIndex: 2,
            }}
            title="Vimeo video"
          />
        )}
      </div>
    );
  }

  // Self-hosted video (MP4, WebM)
  return (
    <div ref={containerRef} className={className} style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
      {hasError ? (
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#1a1a2e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ef4444',
          }}
        >
          <span>Video failed to load</span>
        </div>
      ) : (
        isVisible && (
          <video
            ref={videoRef}
            src={url}
            autoPlay={autoplay}
            muted={muted}
            loop={loop}
            controls={controls}
            poster={poster}
            playsInline
            onPlay={onPlay}
            onPause={onPause}
            onEnded={onEnded}
            onError={() => setHasError(true)}
            onLoadedData={() => setIsLoaded(true)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )
      )}
    </div>
  );
}

// Export helper functions for external use
export { detectVideoType, extractYouTubeId, extractVimeoId };
export default VideoPlayer;
